import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Save,
    CreditCard,
    Calendar,
    DollarSign,
    AlertCircle,
    CheckCircle,
    Users
} from 'lucide-react';
import { useGetMembershipPlanQuery, useUpdateMembershipPlanMutation, UpdateMembershipPlanRequest, MembershipPlan } from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';
import { Button } from '../../components/ui/Button';

const EditMembershipPlan: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const planId = id ? parseInt(id) : null;
    const isValidId = planId && !isNaN(planId) && planId > 0;

    const { data: planResponse, isLoading, error } = useGetMembershipPlanQuery(planId!, {
        skip: !isValidId,
        refetchOnMountOrArgChange: true
    });
    const [updatePlan, { isLoading: isSubmitting }] = useUpdateMembershipPlanMutation();

    const plan: MembershipPlan | undefined = planResponse?.data;

    const [formData, setFormData] = useState<UpdateMembershipPlanRequest>({
        plan_id: planId || 0,
        name: '',
        description: null,
        price: 0,
        duration_days: 30,
        payment_type: 'one_time',
        monthly_installment_amount: null,
        monthly_installment_count: null,
        access_level: null,
        is_active: true,
        max_bookings_per_month: 0
    });

    const [errors, setErrors] = useState<Partial<Record<keyof UpdateMembershipPlanRequest, string>>>({});

    // Populate form with plan data when loaded
    useEffect(() => {
        if (plan) {
            setFormData({
                plan_id: plan.plan_id,
                name: plan.name,
                description: plan.description || null,
                price: typeof plan.price === 'string' ? parseFloat(plan.price) : plan.price,
                duration_days: plan.duration_days,
                payment_type: plan.payment_type,
                monthly_installment_amount: plan.monthly_installment_amount 
                    ? (typeof plan.monthly_installment_amount === 'string' 
                        ? parseFloat(plan.monthly_installment_amount) 
                        : plan.monthly_installment_amount)
                    : null,
                monthly_installment_count: plan.monthly_installment_count || null,
                access_level: plan.access_level || null,
                is_active: plan.is_active,
                max_bookings_per_month: plan.max_bookings_per_month
            });
        }
    }, [plan]);

    const handleInputChange = (field: keyof UpdateMembershipPlanRequest, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof UpdateMembershipPlanRequest, string>> = {};

        if (!formData.name || !formData.name.trim()) {
            newErrors.name = 'Plan name is required';
        }

        if (formData.price !== undefined && (formData.price <= 0)) {
            newErrors.price = 'Price must be greater than 0';
        }

        if (formData.duration_days !== undefined && (formData.duration_days <= 0)) {
            newErrors.duration_days = 'Duration must be greater than 0';
        }

        if (formData.payment_type === 'monthly_installment') {
            if (formData.monthly_installment_amount !== undefined && formData.monthly_installment_amount !== null && formData.monthly_installment_amount <= 0) {
                newErrors.monthly_installment_amount = 'Monthly installment amount must be greater than 0';
            }
            if (formData.monthly_installment_count !== undefined && formData.monthly_installment_count !== null && formData.monthly_installment_count <= 0) {
                newErrors.monthly_installment_count = 'Monthly installment count must be greater than 0';
            }
        }

        if (formData.max_bookings_per_month !== undefined && formData.max_bookings_per_month < 0) {
            newErrors.max_bookings_per_month = 'Max bookings cannot be negative';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (!plan) return;

        try {
            const payload: UpdateMembershipPlanRequest = {
                plan_id: plan.plan_id,
                name: formData.name,
                description: formData.description || null,
                price: formData.price,
                duration_days: formData.duration_days,
                payment_type: formData.payment_type,
                monthly_installment_amount: formData.payment_type === 'monthly_installment' 
                    ? formData.monthly_installment_amount || null 
                    : null,
                monthly_installment_count: formData.payment_type === 'monthly_installment' 
                    ? formData.monthly_installment_count || null 
                    : null,
                access_level: formData.access_level || null,
                is_active: formData.is_active,
                max_bookings_per_month: formData.max_bookings_per_month || 0
            };

            await updatePlan(payload).unwrap();
            toast.success('Membership plan updated successfully!');
            navigate('/admin/membership-management');
        } catch (error: any) {
            console.error('Failed to update membership plan:', error);
            toast.error(error?.data?.message || 'Failed to update membership plan');
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading membership plan data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Plan</h2>
                    <p className="text-gray-600 mb-6">
                        {(error as any)?.data?.message || 'There was an error loading the membership plan details. Please try again.'}
                    </p>
                    <button
                        onClick={() => navigate('/admin/membership-management')}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        ← Back to Membership Plans
                    </button>
                </div>
            </div>
        );
    }

    if (!plan) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Plan Not Found</h2>
                    <p className="text-gray-600 mb-6">The membership plan you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate('/admin/membership-management')}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        ← Back to Membership Plans
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/membership-management')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Edit Membership Plan</h1>
                                <p className="text-gray-600">Update membership plan information</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Basic Information */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <CreditCard className="w-5 h-5 mr-2" />
                                    Basic Information
                                </h2>
                            </div>
                            <div className="p-6 space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Plan Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.name ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                        placeholder="e.g., Gold Membership"
                                    />
                                    {errors.name && (
                                        <p className="mt-1 text-sm text-red-600 flex items-center">
                                            <AlertCircle className="w-4 h-4 mr-1" />
                                            {errors.name}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.description || ''}
                                        onChange={(e) => handleInputChange('description', e.target.value || null)}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Describe the membership plan and its benefits..."
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Price (USD) *
                                        </label>
                                        <div className="relative">
                                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={formData.price}
                                                onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                                                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.price ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="0.00"
                                            />
                                        </div>
                                        {errors.price && (
                                            <p className="mt-1 text-sm text-red-600 flex items-center">
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                {errors.price}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Duration (Days) *
                                        </label>
                                        <div className="relative">
                                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.duration_days}
                                                onChange={(e) => handleInputChange('duration_days', parseInt(e.target.value) || 0)}
                                                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.duration_days ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="30"
                                            />
                                        </div>
                                        {errors.duration_days && (
                                            <p className="mt-1 text-sm text-red-600 flex items-center">
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                {errors.duration_days}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Payment Type *
                                    </label>
                                    <select
                                        value={formData.payment_type}
                                        onChange={(e) => handleInputChange('payment_type', e.target.value as 'one_time' | 'monthly_installment')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="one_time">One Time Payment</option>
                                        <option value="monthly_installment">Monthly Installment</option>
                                    </select>
                                </div>

                                {formData.payment_type === 'monthly_installment' && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-white border border-gray-200 rounded-lg">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Monthly Installment Amount (USD) *
                                            </label>
                                            <div className="relative">
                                                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={formData.monthly_installment_amount || ''}
                                                    onChange={(e) => handleInputChange('monthly_installment_amount', parseFloat(e.target.value) || null)}
                                                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                        errors.monthly_installment_amount ? 'border-red-300' : 'border-gray-300'
                                                    }`}
                                                    placeholder="1000"
                                                />
                                            </div>
                                            {errors.monthly_installment_amount && (
                                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                                    <AlertCircle className="w-4 h-4 mr-1" />
                                                    {errors.monthly_installment_amount}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Number of Installments *
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={formData.monthly_installment_count || ''}
                                                onChange={(e) => handleInputChange('monthly_installment_count', parseInt(e.target.value) || null)}
                                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.monthly_installment_count ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="12"
                                            />
                                            {errors.monthly_installment_count && (
                                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                                    <AlertCircle className="w-4 h-4 mr-1" />
                                                    {errors.monthly_installment_count}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Access Level
                                        </label>
                                        <select
                                            value={formData.access_level || ''}
                                            onChange={(e) => handleInputChange('access_level', e.target.value || null)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="">None</option>
                                            <option value="silver">Silver</option>
                                            <option value="gold">Gold</option>
                                            <option value="platinum">Platinum</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Max Bookings Per Month
                                        </label>
                                        <div className="relative">
                                            <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            <input
                                                type="number"
                                                min="0"
                                                value={formData.max_bookings_per_month}
                                                onChange={(e) => handleInputChange('max_bookings_per_month', parseInt(e.target.value) || 0)}
                                                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                                    errors.max_bookings_per_month ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="0 (for unlimited)"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Enter 0 for unlimited bookings
                                        </p>
                                        {errors.max_bookings_per_month && (
                                            <p className="mt-1 text-sm text-red-600 flex items-center">
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                {errors.max_bookings_per_month}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Status Settings */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <CheckCircle className="w-5 h-5 mr-2" />
                                    Status Settings
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="is_active"
                                        checked={formData.is_active}
                                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                                        Active (Plan is available for purchase)
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500 mt-4">
                                    Active plans are visible and available for users to purchase.
                                </p>
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex items-center justify-end space-x-4">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/admin/membership-management')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        Update Plan
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditMembershipPlan;

